import { addDaysYmd, daysInclusive, todayYmd } from './period';

function seeded(n) {
  const x = Math.sin(n) * 10000;
  return x - Math.floor(x);
}

export function mockSummary({ from, to, compare }) {
  const days = daysInclusive(from, to) || 1;
  const scale = Math.max(1, days / 30);
  const revenue = Math.round(1840000 * scale);
  const returnsCredited = Math.round(24000 * scale);
  return {
    as_of: new Date().toISOString(),
    range: { from, to, tz: 'Asia/Kolkata' },
    channel: 'ALL',
    profit_basis: 'line_cost_snapshot',
    kpis: {
      revenue,
      net_revenue: revenue - returnsCredited,
      orders: Math.round(1286 * scale),
      items_sold: Math.round(4120 * scale),
      aov: 1432.35,
      discounts: Math.round(52000 * scale),
      delivery_fees: Math.round(38000 * scale),
      returns_credited: returnsCredited,
      cash_collected: Math.round(890000 * scale),
      store_credit_used: Math.round(45000 * scale),
      gross_profit: Math.round(410000 * scale),
      profit_margin_pct: 22.3,
    },
    delta: compare
      ? {
          revenue_pct: 12.0,
          net_revenue_pct: 11.0,
          // null when prev and cur are both 0 (or otherwise undefined) — FE shows —
          orders_pct: null,
          aov_pct: -2.0,
          // prev 0 and cur > 0 → 100.0
          gross_profit_pct: 100.0,
        }
      : undefined,
  };
}

export function mockTimeseries({ from, to, channel, granularity = 'day', metric = 'revenue', compare }) {
  const days = Math.max(1, daysInclusive(from, to));
  let count;
  if (granularity === 'hour') {
    count = days * 24;
  } else if (granularity === 'week') {
    count = Math.ceil(days / 7);
  } else {
    count = days;
  }

  const series = [];
  const previous = [];
  const pad = (n) => String(n).padStart(2, '0');

  for (let i = 0; i < count; i += 1) {
    let t;
    if (granularity === 'hour') {
      const dayOffset = Math.floor(i / 24);
      const hour = i % 24;
      t = `${addDaysYmd(from, dayOffset)}T${pad(hour)}`;
    } else if (granularity === 'week') {
      t = addDaysYmd(from, i * 7);
    } else {
      t = addDaysYmd(from, i);
    }

    const base = metric === 'orders' ? 30 + seeded(i + 1) * 40 : 40000 + seeded(i + 2) * 80000;
    const scale = granularity === 'hour' ? 1 / 18 : 1;
    const POS = Math.round(base * scale * (0.28 + seeded(i + 3) * 0.1));
    const DELIVERY = Math.round(base * scale * (0.55 + seeded(i + 4) * 0.15));

    const ch = String(channel || 'all').toLowerCase();
    if (ch === 'pos') {
      series.push({ t, POS });
    } else if (ch === 'delivery') {
      series.push({ t, DELIVERY });
    } else {
      series.push({ t, POS, DELIVERY });
    }

    // previous[] may be shorter than series[] — skip last ~15% of offsets
    if (compare && i < Math.floor(count * 0.85)) {
      if (ch === 'pos') {
        previous.push({ offset: i, POS: Math.round(POS * 0.9) });
      } else if (ch === 'delivery') {
        previous.push({ offset: i, DELIVERY: Math.round(DELIVERY * 0.9) });
      } else {
        previous.push({
          offset: i,
          POS: Math.round(POS * 0.9),
          DELIVERY: Math.round(DELIVERY * 0.88),
        });
      }
    }
  }

  return { metric, granularity, series, previous: compare ? previous : [] };
}

export function mockChannels() {
  return {
    total_revenue: 1840000,
    channels: [
      { channel: 'DELIVERY', orders: 874, revenue: 1250000, share_pct: 67.9, aov: 1430.21 },
      { channel: 'POS', orders: 412, revenue: 590000, share_pct: 32.1, aov: 1432.04 },
    ],
  };
}

export function mockPayments() {
  return {
    collected_total: 1795000,
    methods: [
      { method: 'COD', collected: 1100000 },
      { method: 'UPI', collected: 660000 },
      { method: 'CASH', collected: 210000 },
      { method: 'CARD', collected: 80000 },
      { method: 'CREDIT', collected: 45000 },
    ],
  };
}

export function mockTopProducts({ metric = 'revenue' } = {}) {
  const rows = [
    { rank: 1, name: 'A2 Ghee 500ml', sku: 'GH-500', qty: 420, revenue: 189000, gross_profit: 42000, margin_pct: 22.2 },
    { rank: 2, name: 'Cold-pressed Coconut Oil', sku: 'CO-1L', qty: 310, revenue: 142600, gross_profit: 31000, margin_pct: 21.7 },
    { rank: 3, name: 'Organic Turmeric', sku: 'TU-250', qty: 580, revenue: 98600, gross_profit: 28000, margin_pct: 28.4 },
    { rank: 4, name: 'Forest Honey 250g', sku: 'HN-250', qty: 265, revenue: 87450, gross_profit: 19800, margin_pct: 22.6 },
    { rank: 5, name: 'Millet Mix 1kg', sku: 'ML-1K', qty: 198, revenue: 71280, gross_profit: 16500, margin_pct: 23.1 },
    { rank: 6, name: 'Jaggery Cubes', sku: 'JG-500', qty: 340, revenue: 61200, gross_profit: 14200, margin_pct: 23.2 },
    { rank: 7, name: 'Raw Peanuts 500g', sku: 'PN-500', qty: 410, revenue: 57400, gross_profit: 9800, margin_pct: 17.1 },
    { rank: 8, name: 'Sesame Oil 500ml', sku: 'SO-500', qty: 155, revenue: 54250, gross_profit: 12100, margin_pct: 22.3 },
    { rank: 9, name: 'Mustard Oil 1L', sku: 'MO-1L', qty: 148, revenue: 51800, gross_profit: 11000, margin_pct: 21.2 },
    { rank: 10, name: 'Rock Salt 1kg', sku: 'RS-1K', qty: 520, revenue: 46800, gross_profit: 14200, margin_pct: 30.3 },
    { rank: 11, name: 'Brown Sugar 1kg', sku: 'BS-1K', qty: 390, revenue: 42900, gross_profit: 7800, margin_pct: 18.2 },
    { rank: 12, name: 'Wheat Flour 5kg', sku: 'WF-5K', qty: 112, revenue: 40320, gross_profit: 7200, margin_pct: 17.9 },
    { rank: 13, name: 'Toor Dal 1kg', sku: 'TD-1K', qty: 205, revenue: 38950, gross_profit: 6900, margin_pct: 17.7 },
    { rank: 14, name: 'Green Moong 500g', sku: 'GM-500', qty: 278, revenue: 36140, gross_profit: 8100, margin_pct: 22.4 },
    { rank: 15, name: 'Cashew Whole 250g', sku: 'CW-250', qty: 96, revenue: 33600, gross_profit: 5400, margin_pct: 16.1 },
    { rank: 16, name: 'Almonds 250g', sku: 'AL-250', qty: 88, revenue: 31680, gross_profit: 5100, margin_pct: 16.1 },
    { rank: 17, name: 'Raisins 200g', sku: 'RA-200', qty: 174, revenue: 29580, gross_profit: 6200, margin_pct: 21.0 },
    { rank: 18, name: 'Dates Medjool 250g', sku: 'DT-250', qty: 102, revenue: 27540, gross_profit: 5800, margin_pct: 21.1 },
    { rank: 19, name: 'Chia Seeds 200g', sku: 'CS-200', qty: 141, revenue: 25380, gross_profit: 7100, margin_pct: 28.0 },
    { rank: 20, name: 'Flax Seeds 200g', sku: 'FS-200', qty: 133, revenue: 23940, gross_profit: 6800, margin_pct: 28.4 },
  ];

  const sorted = [...rows].sort((a, b) => {
    if (metric === 'qty') return b.qty - a.qty;
    if (metric === 'profit') return b.gross_profit - a.gross_profit;
    return b.revenue - a.revenue;
  });

  return { metric, items: sorted.map((r, i) => ({ ...r, rank: i + 1 })) };
}

export function mockCategories({ metric = 'revenue', limit = 5 } = {}) {
  const rows = [
    { category: 'Oils & Ghee', qty: 980, revenue: 520000, gross_profit: 112000, margin_pct: 21.5 },
    { category: 'Spices', qty: 1240, revenue: 310000, gross_profit: 82000, margin_pct: 26.4 },
    { category: 'Grains', qty: 760, revenue: 245000, gross_profit: 48000, margin_pct: 19.6 },
    { category: 'Sweeteners', qty: 540, revenue: 168000, gross_profit: 39000, margin_pct: 23.2 },
    { category: 'Snacks', qty: 890, revenue: 142000, gross_profit: 28000, margin_pct: 19.7 },
    { category: 'Beverages', qty: 720, revenue: 128000, gross_profit: 26000, margin_pct: 20.3 },
    { category: 'Dairy', qty: 610, revenue: 118000, gross_profit: 22000, margin_pct: 18.6 },
    { category: 'Personal Care', qty: 430, revenue: 96000, gross_profit: 21000, margin_pct: 21.9 },
  ];
  const sorted = [...rows].sort((a, b) => {
    if (metric === 'qty') return b.qty - a.qty;
    if (metric === 'profit') return b.gross_profit - a.gross_profit;
    return b.revenue - a.revenue;
  });
  return { metric, items: sorted.slice(0, limit) };
}

export function mockGst() {
  return {
    gst_inclusive: true,
    filing: false,
    rows: [
      { rate: 0, taxable: 100000, tax: 0, cgst: 0, sgst: 0 },
      { rate: 5, taxable: 400000, tax: 19048, cgst: 9524, sgst: 9524 },
      { rate: 18, taxable: 900000, tax: 137288, cgst: 68644, sgst: 68644 },
      { rate: 40, taxable: 190000, tax: 54286, cgst: 27143, sgst: 27143 },
    ],
    totals: { taxable: 1590000, tax: 210622, cgst: 105311, sgst: 105311 },
  };
}

export function mockReturns() {
  return {
    credits_issued: 24000,
    credit_count: 38,
    exchange_count: 12,
    failed_pickup_count: 5,
  };
}

function buildSeries(from, to, granularity, mapFn) {
  const days = Math.max(1, daysInclusive(from, to));
  let count;
  if (granularity === 'hour') count = days * 24;
  else if (granularity === 'week') count = Math.ceil(days / 7);
  else count = days;
  const pad = (n) => String(n).padStart(2, '0');
  const series = [];
  for (let i = 0; i < count; i += 1) {
    let t;
    if (granularity === 'hour') {
      const dayOffset = Math.floor(i / 24);
      const hour = i % 24;
      t = `${addDaysYmd(from, dayOffset)}T${pad(hour)}`;
    } else if (granularity === 'week') {
      t = addDaysYmd(from, i * 7);
    } else {
      t = addDaysYmd(from, i);
    }
    series.push({ t, ...mapFn(i) });
  }
  return series;
}

/** Returns & exchange counts — GET /store/dashboard/returns/timeseries */
export function mockReturnsTimeseries({ from, to, channel, granularity = 'day' }) {
  const ch = String(channel || 'all').toLowerCase();
  if (ch === 'pos') {
    return { granularity, series: [], applicable: false };
  }
  return {
    granularity,
    applicable: true,
    series: buildSeries(from, to, granularity, (i) => ({
      returns: Math.round(2 + seeded(i + 11) * 8),
      exchanges: Math.round(1 + seeded(i + 17) * 6),
    })),
  };
}

/** Fulfillment / scheduling — GET /store/dashboard/fulfillment/timeseries */
export function mockFulfillmentTimeseries({ from, to, channel, granularity = 'day' }) {
  const ch = String(channel || 'all').toLowerCase();
  if (ch === 'pos') {
    return { applicable: false, granularity, series: [] };
  }
  return {
    applicable: true,
    granularity,
    series: buildSeries(from, to, granularity, (i) => {
      const scheduled = Math.round(18 + seeded(i + 21) * 22);
      const failed = Math.round(1 + seeded(i + 37) * Math.max(1, scheduled * 0.12));
      const completed = Math.max(0, Math.round(scheduled * (0.72 + seeded(i + 29) * 0.18) - failed * 0.3));
      return { scheduled, completed, failed };
    }),
  };
}

/** @deprecated */
export const mockReturnsRefundsTimeseries = mockReturnsTimeseries;
/** @deprecated */
export const mockSchedulingTimeseries = mockFulfillmentTimeseries;

export function mockTemplates(isAdmin) {
  const all = [
    { type: 'sales_summary', label: 'Sales summary', admin_only: false },
    { type: 'sales_detail', label: 'Sales detail', admin_only: false },
    { type: 'profit_by_sku', label: 'Profit by SKU', admin_only: true },
    { type: 'gst_breakup', label: 'GST breakup', admin_only: false },
    { type: 'payments', label: 'Payments', admin_only: false },
    { type: 'returns', label: 'Returns', admin_only: false },
  ];
  return { templates: isAdmin ? all : all.filter((t) => !t.admin_only) };
}

let jobSeq = 3;
const jobs = [
  {
    id: 'rpt_1',
    type: 'sales_summary',
    from: '2026-08-01',
    to: '2026-08-31',
    channel: 'ALL',
    format: 'csv',
    status: 'READY',
    created_at: new Date(Date.now() - 3600000).toISOString(),
    error: null,
    expires_at: null,
  },
  {
    id: 'rpt_2',
    type: 'gst_breakup',
    from: '2026-08-01',
    to: '2026-08-31',
    channel: 'ALL',
    format: 'pdf',
    status: 'READY',
    created_at: new Date(Date.now() - 7200000).toISOString(),
    error: null,
    expires_at: null,
  },
  {
    id: 'rpt_3',
    type: 'profit_by_sku',
    from: todayYmd(),
    to: todayYmd(),
    channel: 'POS',
    format: 'csv',
    status: 'RUNNING',
    created_at: new Date(Date.now() - 60000).toISOString(),
    error: null,
    expires_at: null,
  },
];

export function mockListReports() {
  return { reports: [...jobs] };
}

const PENDING = new Set(['RUNNING']);
const pollTicks = Object.create(null);

export function mockGetReport(id) {
  const row = jobs.find((j) => j.id === id);
  if (!row) {
    const err = new Error('Report not found');
    err.code = 'not_found';
    throw err;
  }

  if (PENDING.has(row.status)) {
    pollTicks[id] = (pollTicks[id] || 0) + 1;
    if (pollTicks[id] >= 2) {
      row.status = 'READY';
      row.error = null;
      // Job-level expires_at stays null; real expiry is only on GET .../download
      row.expires_at = null;
    }
  }

  return { report: { ...row } };
}

export function mockCreateReport({ type, from, to, channel, format }) {
  jobSeq += 1;
  const job = {
    id: `rpt_${jobSeq}`,
    type,
    from,
    to,
    channel,
    format,
    status: 'READY',
    created_at: new Date().toISOString(),
    error: null,
    expires_at: null,
  };
  jobs.unshift(job);
  pollTicks[job.id] = 0;
  return { report: { ...job } };
}

/** Sample tabular rows for mock report downloads (CSV + PDF). */
function mockReportRows(job) {
  const type = job?.type || 'sales_summary';
  const from = job?.from || '';
  const to = job?.to || '';
  const channel = job?.channel || 'ALL';

  if (type === 'gst_breakup') {
    return {
      title: 'GST breakup',
      headers: ['Rate', 'Taxable', 'CGST', 'SGST', 'Tax'],
      rows: [
        ['5%', '420000', '10500', '10500', '21000'],
        ['12%', '310000', '18600', '18600', '37200'],
        ['18%', '180000', '16200', '16200', '32400'],
        ['Total', '910000', '45300', '45300', '90600'],
      ],
      meta: [
        ['Report', 'GST breakup'],
        ['Range', `${from} to ${to}`],
        ['Channel', channel],
      ],
    };
  }

  if (type === 'profit_by_sku') {
    return {
      title: 'Profit by SKU',
      headers: ['SKU', 'Name', 'Qty', 'Revenue', 'Gross profit', 'Margin %'],
      rows: [
        ['GH-500', 'A2 Ghee 500ml', '420', '189000', '42000', '22.2'],
        ['CO-1L', 'Cold-pressed Coconut Oil', '310', '142600', '31000', '21.7'],
        ['TU-250', 'Organic Turmeric', '580', '98600', '28000', '28.4'],
        ['HN-250', 'Forest Honey 250g', '265', '87450', '19800', '22.6'],
      ],
      meta: [
        ['Report', 'Profit by SKU'],
        ['Range', `${from} to ${to}`],
        ['Channel', channel],
      ],
    };
  }

  if (type === 'payments') {
    return {
      title: 'Payments',
      headers: ['Method', 'Collected', 'Orders'],
      rows: [
        ['CASH', '890000', '512'],
        ['UPI', '620000', '418'],
        ['CARD', '210000', '146'],
        ['CREDIT', '45000', '38'],
      ],
      meta: [
        ['Report', 'Payments'],
        ['Range', `${from} to ${to}`],
        ['Channel', channel],
      ],
    };
  }

  if (type === 'returns') {
    return {
      title: 'Returns',
      headers: ['Metric', 'Value'],
      rows: [
        ['Credits issued', '24000'],
        ['Credit count', '18'],
        ['Exchange count', '7'],
        ['Failed pickup count', '3'],
      ],
      meta: [
        ['Report', 'Returns'],
        ['Range', `${from} to ${to}`],
        ['Channel', channel],
      ],
    };
  }

  if (type === 'sales_detail') {
    return {
      title: 'Sales detail',
      headers: ['Date', 'Order', 'Channel', 'Items', 'Amount'],
      rows: [
        [from || '2026-08-01', 'ORD-1001', 'POS', '3', '1450'],
        [from || '2026-08-01', 'ORD-1002', 'DELIVERY', '5', '2380'],
        [to || '2026-08-02', 'ORD-1003', 'POS', '2', '890'],
        [to || '2026-08-02', 'ORD-1004', 'DELIVERY', '4', '1760'],
      ],
      meta: [
        ['Report', 'Sales detail'],
        ['Range', `${from} to ${to}`],
        ['Channel', channel],
      ],
    };
  }

  // sales_summary (default)
  return {
    title: 'Sales summary',
    headers: ['Metric', 'Value'],
    rows: [
      ['Revenue', '1840000'],
      ['Net revenue', '1816000'],
      ['Orders', '1286'],
      ['Items sold', '4120'],
      ['AOV', '1432.35'],
      ['Gross profit', '410000'],
      ['Profit margin %', '22.3'],
    ],
    meta: [
      ['Report', 'Sales summary'],
      ['Range', `${from} to ${to}`],
      ['Channel', channel],
    ],
  };
}

/** Escape PDF literal string (WinAnsi / Helvetica). ASCII only. */
function pdfEscape(text) {
  return String(text)
    .replace(/[^\x20-\x7E]/g, ' ')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

/**
 * Build a minimal valid PDF (with xref) so browsers actually render text.
 * Blob must be binary Latin-1 bytes — never UTF-8-encode the PDF string.
 */
function buildMockPdf(report, reportId) {
  const lines = [
    report.title,
    `Report ID: ${reportId}`,
    ...report.meta.map(([k, v]) => `${k}: ${v}`),
    '',
    report.headers.join(' | '),
    '-'.repeat(72),
    ...report.rows.map((row) => row.join(' | ')),
    '',
    'Mock analytics report - sample data for local download testing',
  ];

  let y = 750;
  const ops = ['BT', '/F1 11 Tf', '14 TL', `50 ${y} Td`];
  lines.forEach((line, i) => {
    const safe = pdfEscape(line).slice(0, 110);
    if (i === 0) {
      ops.push('/F1 16 Tf', `(${safe}) Tj`, '/F1 11 Tf', 'T*');
    } else {
      ops.push(`(${safe}) Tj`, 'T*');
    }
  });
  ops.push('ET');
  const stream = ops.join('\n');

  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>',
    `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ];

  let body = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((obj, i) => {
    offsets.push(body.length);
    body += `${i + 1} 0 obj\n${obj}\nendobj\n`;
  });

  const xrefPos = body.length;
  let xref = `xref\n0 ${objects.length + 1}\n`;
  xref += '0000000000 65535 f \n';
  for (let i = 1; i <= objects.length; i += 1) {
    xref += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  }
  body += xref;
  body += `trailer<< /Size ${objects.length + 1} /Root 1 0 R >>\n`;
  body += `startxref\n${xrefPos}\n%%EOF\n`;

  const bytes = new Uint8Array(body.length);
  for (let i = 0; i < body.length; i += 1) {
    bytes[i] = body.charCodeAt(i) & 0xff;
  }
  return new Blob([bytes], { type: 'application/pdf' });
}

/** Build a real in-browser file so Download works without a backend. */
export function mockDownloadUrl(id) {
  const job = jobs.find((j) => j.id === id);
  const format = String(job?.format || 'csv').toLowerCase();
  const report = mockReportRows(job);

  let blob;
  if (format === 'pdf') {
    blob = buildMockPdf(report, id);
  } else {
    const lines = [
      ...report.meta,
      [],
      report.headers,
      ...report.rows,
    ];
    const csv = `\uFEFF${lines
      .map((row) =>
        (Array.isArray(row) ? row : [row])
          .map((c) => `"${String(c).replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\r\n')}\r\n`;
    blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  }

  return {
    download_url: URL.createObjectURL(blob),
    content_type: format === 'pdf' ? 'application/pdf' : 'text/csv',
    expires_at: new Date(Date.now() + 900000).toISOString(),
  };
}
